"use client";

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BookOpenIcon,
  CpuIcon,
  LayoutTemplateIcon,
  ShieldIcon,
  SlidersHorizontalIcon,
  UserIcon,
  WrenchIcon,
} from "lucide-react";
import type { SettingsView } from "@/components/custom/contexts/routeContext";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { useUserSettings } from "@/components/custom/contexts/userSettingsContext";
import { cn } from "@/lib/utils";
import { SettingsAiContent } from "./settings-ai-content";
import { SettingsDevToolsContent } from "./settings-dev-tools-content";
import { SkillCreate } from "./skill-create";
import { SkillDetail } from "./skill-detail";
import { SkillsList } from "./skills-list";
import { TemplatesList } from "./templates-list";
import { TenantSettingsContent } from "./tenant-settings-content";
import { UserPreferencesContent } from "./user-preferences-content";

type NavEntry =
  | {
      kind: "link";
      id: string;
      labelKey: string;
      icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
      href: string;
      visible: boolean;
      indent?: boolean;
      devOnly?: boolean;
      matches: SettingsView[];
    }
  | {
      kind: "section";
      id: string;
      labelKey: string;
      icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
      visible: boolean;
    }
  | {
      kind: "divider";
      id: string;
      visible: boolean;
    };

export function SettingsShell({
  view,
  skillId,
}: {
  view: SettingsView;
  skillId?: string;
}) {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const { settings } = useUserSettings();
  const isDev = !settings.isProductionEnvironment;

  const navEntries: NavEntry[] = [
    {
      kind: "link",
      id: "dev-tools",
      labelKey: "settings.nav.devTools",
      icon: WrenchIcon,
      href: "/settings/dev-tools",
      visible: isDev,
      devOnly: true,
      matches: ["dev-tools"],
    },
    {
      kind: "divider",
      id: "dev-divider",
      visible: isDev,
    },
    {
      kind: "link",
      id: "ai",
      labelKey: "settings.nav.general",
      icon: CpuIcon,
      href: "/settings/ai",
      visible: true,
      matches: ["ai"],
    },
    {
      kind: "link",
      id: "user",
      labelKey: "settings.nav.user",
      icon: UserIcon,
      href: "/settings/user",
      visible: true,
      matches: ["user"],
    },
    {
      kind: "link",
      id: "skills",
      labelKey: "settings.nav.skills",
      icon: BookOpenIcon,
      href: "/settings/skills",
      visible: true,
      matches: ["skills", "skills-detail", "skills-create"],
    },
    {
      kind: "link",
      id: "templates",
      labelKey: "settings.nav.templates",
      icon: LayoutTemplateIcon,
      href: "/settings/templates",
      visible: true,
      matches: ["templates"],
    },
    {
      kind: "section",
      id: "admin-section",
      labelKey: "settings.nav.administration",
      icon: ShieldIcon,
      visible: isAdmin,
    },
    {
      kind: "link",
      id: "admin-skills",
      labelKey: "settings.nav.adminSkills",
      icon: BookOpenIcon,
      href: "/settings/admin/skills",
      visible: isAdmin,
      indent: true,
      matches: ["admin-skills", "admin-skills-detail", "admin-skills-create"],
    },
    {
      kind: "link",
      id: "admin-preferences",
      labelKey: "settings.nav.adminPreferences",
      icon: SlidersHorizontalIcon,
      href: "/settings/admin/preferences",
      visible: isAdmin,
      indent: true,
      matches: ["admin-preferences"],
    },
  ];

  return (
    <div className="flex h-full w-full">
      <nav className="sticky top-0 flex h-screen w-[240px] shrink-0 flex-col overflow-y-auto border-border/60 border-r bg-background px-3 py-6">
        <div className="flex flex-col gap-1">
          {navEntries
            .filter((entry) => entry.visible)
            .map((entry) => {
              if (entry.kind === "divider") {
                return (
                  <hr
                    key={entry.id}
                    className="my-3 border-border/60 border-dashed"
                  />
                );
              }

              if (entry.kind === "section") {
                return (
                  <div key={entry.id}>
                    <hr className="my-3 border-border/60" />
                    <div className="mb-1 flex items-center gap-2 px-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground select-none">
                      <entry.icon
                        className="size-[13px] shrink-0"
                        strokeWidth={1.7}
                      />
                      <span>{t(entry.labelKey)}</span>
                    </div>
                  </div>
                );
              }

              const isActive = entry.matches.includes(view);
              return (
                <Link
                  key={entry.id}
                  to={entry.href}
                  className={cn(
                    "flex items-center gap-2.5 rounded-md px-2.5 py-2 font-medium text-[13.5px] transition-colors",
                    entry.indent && "ml-4",
                    entry.devOnly && "opacity-70 border border-dashed border-amber-500/40",
                    isActive
                      ? "bg-accent/40 text-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                  aria-current={isActive ? "page" : undefined}
                >
                  <entry.icon
                    className={cn(
                      "size-[15px] shrink-0",
                      entry.devOnly && "text-amber-500",
                    )}
                    strokeWidth={1.7}
                  />
                  <span>{t(entry.labelKey)}</span>
                </Link>
              );
            })}
        </div>
      </nav>

      <main className="flex-1 overflow-hidden">
        {view === "skills" && <SkillsList mode="personal" />}
        {view === "skills-detail" && <SkillDetail id={skillId} mode="personal" />}
        {view === "skills-create" && <SkillCreate mode="personal" />}
        {view === "templates" && <TemplatesList />}
        {view === "ai" && <SettingsAiContent />}
        {view === "user" && <UserPreferencesContent />}
        {view === "dev-tools" && <SettingsDevToolsContent />}
        {view === "admin-skills" && <SkillsList mode="admin" />}
        {view === "admin-skills-detail" && <SkillDetail id={skillId} mode="admin" />}
        {view === "admin-skills-create" && <SkillCreate mode="admin" />}
        {view === "admin-preferences" && <TenantSettingsContent />}
      </main>
    </div>
  );
}
